import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Entsorger, mergeKundenNrVvvosWithBetriebe } from '../entsorger';
import { MessageService } from '../../../../services/message-service/message.service';
import { StammdatenService } from '../../stammdaten.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KieneAutocompleteComponent } from '../../../kiene-autocomplete/kiene-autocomplete.component';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { Client } from '../../../client/classes/client';
import { EntsorgerAnlegenDialogComponent } from '../entsorger-anlegen-dialog/entsorger-anlegen-dialog.component';
import { AppService } from '../../../../services/app/app.service';

@Component({
    selector: 'kiene-entsorger-details',
    templateUrl: './entsorger-details.component.html',
    styleUrls: ['./entsorger-details.component.scss'],
})
export class EntsorgerDetailsComponent implements OnInit, OnDestroy {
    entsorgerSubscription: Subscription;

    entsorgerPath = `${this.apiBaseUrl}entsorger/read_kundenportal.php?favorit=0`;
    isLoading = false;

    @ViewChild('nameAutocomplete') nameAutocomplete: KieneAutocompleteComponent;
    @Input('dialog') showAsDialog = false;

    @Input('entsorger') entsorger: Entsorger;
    buttonText = 'Anlegen';
    redirectTo: string;
    headline = 'Entsorger';
    backRoute = '../';

    @Input('redirectBack')
    redirectBack = true;
    ersteinrichtung = false;

    @Output('saved')
    saved: EventEmitter<Entsorger> = new EventEmitter<Entsorger>();

    @Output('deleted')
    deleted: EventEmitter<Entsorger> = new EventEmitter<Entsorger>();

    private vvvoSubscription: Subscription;

    constructor(
        private stammdatenService: StammdatenService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private appService: AppService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnDestroy(): void {
        this.entsorgerSubscription?.unsubscribe();
        this.vvvoSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        // Dialog: Entsorger kommt als Input – VVVO-Liste sofort aus Profil-Betrieben aufbauen
        if (this.entsorger) {
            this.initVvvos();
        }
        this.loadEntsorger();
    }

    redirect(entsorgerCreated?: Entsorger) {
        let destinationPath: string[];
        const queryParams = {};

        if (this.redirectBack) {
            // ersteinrichtung - Kundennr fehlen noch - weiterleiten
            if (this.ersteinrichtung && entsorgerCreated) {
                this.redirectTo = 'registrierung-ersteinrichtung';
                queryParams['ersteinrichtung'] = 1;
                destinationPath = [entsorgerCreated.entsorger_id.toString(), 'kunden-nr'];

                // neu anlage - Kundennr fehlen noch - weiterleiten
            } else if (entsorgerCreated) {
                if (!this.redirectTo) {
                    destinationPath = [entsorgerCreated.entsorger_id.toString(), 'kunden-nr'];
                    queryParams['redirectTo'] = this.redirectTo;
                    this.redirectTo = `einstellungen-stammdaten-details-${entsorgerCreated.entsorger_id}`;
                }
            }

            // kein Entsorger uebergeben update - zurueckleiten
            if (!destinationPath) {
                window.history.back();
            } else {
                console.log('NAVIGATE: ', destinationPath, queryParams);
                this.router.navigate(destinationPath, { relativeTo: this.route, queryParams }).then();
            }
        }
    }

    addOrUpdateEntsorger() {
        this.isLoading = true;
        this.stammdatenService.updateEntsorger(this.entsorger).subscribe({
            next: entsorger => {
                this.entsorger = entsorger;
                this.messageService.infoMessage('Der Entsorger wurde erfolgreich aktualisiert.');
                this.isLoading = false;
                this.emit();
                if (!this.showAsDialog) {
                    this.router.navigate(['../'], { relativeTo: this.route });
                }
            },
            error: err => {
                this.messageService.errorMessage('Beim Aktualisieren des Entsorger ist ein Fehler aufgetreten.', err);
                this.isLoading = false;
            },
        });
    }

    emit(): void {
        this.saved.emit(this.entsorger);
        this.stammdatenService.speichereLetzenAngelegtenEntsorger(this.entsorger);
    }

    selectedEntsorger(entsorger: Entsorger) {
        if (!entsorger) {
            return;
        }
        this.entsorger = entsorger;
        this.initVvvos();
    }

    deleteEntsorgerFavorit(event: MouseEvent) {
        event.stopPropagation();

        const input = new ConfirmDialogInput();
        input.headline = 'Entsorger löschen';
        input.text = `Möchten Sie den Entsorger ${this.entsorger.name} wirklich löschen?`;
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.stammdatenService.deleteEntsorgerFavorit(this.entsorger).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Der Entsorger wurde erfolgreich gelöscht.');

                            this.deleted.emit(this.entsorger);
                            if (!this.showAsDialog) {
                                this.redirect();
                            }
                        },
                        error: (err: any) => {
                            this.messageService.errorMessage('Fehler beim Löschen des Entsorgerfavoriten', err);
                        },
                    });
                }
            },
        });
    }

    createNewEntsorger(entsorgerName?: string) {
        const dialogRef = this.dialog.open(EntsorgerAnlegenDialogComponent, { data: entsorgerName });
        dialogRef.afterClosed().subscribe({
            next: (entsorger: Entsorger) => {
                if (entsorger) {
                    this.selectedEntsorger(entsorger);
                }
            },
        });
    }

    private initVvvos() {
        if (!this.entsorger) {
            return;
        }
        if (!this.entsorger.kundenNrVvvos) {
            this.entsorger.kundenNrVvvos = [];
        }
        this.isLoading = true;
        this.vvvoSubscription?.unsubscribe();
        // Gleicher Datenstand wie Profil → Betriebe (eigene + freigegebene)
        this.vvvoSubscription = this.stammdatenService.ladeMeineUndFreigegebeneVvvos().subscribe({
            next: vvvos => {
                this.isLoading = false;
                const betriebe = <Client[]>(vvvos?.records || []);
                this.entsorger.kundenNrVvvos = mergeKundenNrVvvosWithBetriebe(
                    this.entsorger.kundenNrVvvos,
                    betriebe,
                );
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    private loadEntsorger() {
        const entsorgerId = this.route.snapshot.paramMap.get('id');
        if (entsorgerId) {
            this.stammdatenService.getEntsorger(entsorgerId).subscribe({
                next: entsorger => {
                    this.entsorger = entsorger;
                    this.initVvvos();
                },
            });
        }
    }

    get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }

    goBack(): void {
        window.history.back();
    }
}
